/**
 * The note a parked task shows above its routing block (docs/specs/auto-split-oversized-tasks.md §4.6).
 *
 * The shapes below mirror the taskDetail fields the hub adds for auto-split. They are typed here,
 * optional and local, so this app typechecks before @roomate/core and apps/server ship them. Delete
 * them once the core type carries the real fields.
 */
export type OversizedPark = {
  tier: string;
  size: string;
  points: number;
  reasons: string[];
  splitTask: { id: string; number: number; stage: string } | null;
  pendingDecisionId: string | null;
};

export type SplitRef = { id: string; number: number };

export type ParkDetail = {
  task: { stage: string };
  blockedReason?: "needs_split" | "oversized" | null;
  oversized?: OversizedPark | null;
  splitInto?: SplitRef[] | null;
};

/** A piece of the note: plain text, or a link to another task by its number. */
export type NotePart = string | SplitRef;

export type ParkNote = { text: string; parts: NotePart[] };

function linked(refs: SplitRef[]): NotePart[] {
  return refs.flatMap((r, i): NotePart[] => (i === 0 ? [r] : [", ", r]));
}

/** The note for a task's current park, or null when there is nothing to say. Pure: the Task page renders it outside any disclosure. */
export function parkNote(detail: ParkDetail): ParkNote | null {
  const note = (parts: NotePart[]): ParkNote => ({ text: parts.map((p) => (typeof p === "string" ? p : `#${p.number}`)).join(""), parts });

  if (detail.task.stage === "cancelled" && detail.splitInto?.length) {
    return note(["Split into ", ...linked(detail.splitInto), "."]);
  }

  const o = detail.oversized;
  if (o) {
    if (o.pendingDecisionId) return note(["Split proposed, waiting for approval."]);
    const why = `${o.tier}, ${o.points} points${o.reasons.length ? `: ${o.reasons.join(", ")}` : ""}`;
    if (!o.splitTask) return note([`Too big for one agent run (${why}). Parked; no agent can split it. Split it by hand or name a person.`]);
    const split: SplitRef = { id: o.splitTask.id, number: o.splitTask.number };
    return note([`Too big for one agent run (${why}). Parked until it is split: `, split, "."]);
  }

  if (detail.blockedReason === "needs_split") {
    return note(["Parked: no single agent may build these areas. Split it, or name a person."]);
  }

  return null;
}
